/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutationSendMessage } from 'hooks/useMessagesRelatedProps';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GeneralFunction } from 'types/common';
import { IChatRoom, IMessage } from 'types/messages';
import { v4 as uuidv4 } from 'uuid';

import { useReadMessageCall } from '../helpers';

// interface ITempMessage extends Partial<IMessage> {
//   sent?: boolean;
// }

export const useChatWindowProps = ({
  refetchChatRoomData,
  fetchNextPageMessage,
  onChatEnter,
  chatAppointmentId,
  onChatFocus,
  messages,
  oldMessageLoading,
  oldMessages,
  chatRoom,
}: {
  refetchChatRoomData?: GeneralFunction;
  fetchNextPageMessage?: GeneralFunction;
  loading?: boolean;
  onChatEnter?: GeneralFunction;
  chatAppointmentId?: string;
  onChatFocus?: GeneralFunction;
  messages?: IMessage[];
  oldMessageLoading?: boolean;
  oldMessages?: IMessage[];
  chatRoom?: IChatRoom;
}) => {
  const chatRoomId = chatRoom?.id || '';
  const chatInputHeight = 36;
  const chatImageHeight = 100;
  const scrollPositionRef = useRef(0);
  const isScrollToBottomDisable = useRef<boolean>(false);
  // ref for form - used for reseting form after press Enter
  const formRef = useRef<any>();
  // ref for chat window - used for calculating the chat window height
  const chatWindowRef = useRef<any>();
  const lastWindowScrollHeight = useRef<number>(0);
  // const [tempErrorSentMessages, setTempErrorSentMessages] = useState<Partial<IMessage>[]>([]);

  const chatContainerRef = useRef<any>();
  const chatHeaderRef = useRef<any>();
  const inputRef = useRef<any>();
  // use to calculate chatbox height - due to some difficulties with mobile view (scroll the whole page instead of just the chat box and no limit on container's length) => need to set height as such
  const [chatboxMaxHeight, setChatboxMaxHeight] = useState<number>(0);

  // used for setting chatbox images when press "paste" command (add img to chat box like fb's messenger)
  const [chatboxImages, setChatboxImages] = useState<Record<string, File>>({});

  // auto scroll to bottom when new message arrives
  useEffect(() => {
    if (!messages || !chatWindowRef.current) return;
    if (!isScrollToBottomDisable.current) {
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
      scrollPositionRef.current = chatWindowRef.current.scrollHeight;
    }
    lastWindowScrollHeight.current = chatWindowRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (isScrollToBottomDisable.current) {
      chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight - lastWindowScrollHeight.current);
      lastWindowScrollHeight.current = chatWindowRef.current.scrollHeight;
    }
  }, [oldMessages]);

  // auto scroll to bottom when change appointment thread
  useEffect(() => {
    isScrollToBottomDisable.current = false;
    if (!messages || !chatWindowRef.current) return;
    chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
  }, [chatAppointmentId]);

  // catch "paste" event to get the images to chatbox
  useEffect(() => {
    const getPastedImages = (e: any) => {
      const pastedFile = e?.clipboardData?.files?.[0];
      if (pastedFile) {
        setChatboxImages((c) => ({ ...c, [uuidv4()]: pastedFile }));
      }
    };
    window.addEventListener('paste', getPastedImages);
    return () => window.removeEventListener('paste', getPastedImages);
  }, []);

  // calculate chat box height when resize height
  useEffect(() => {
    const reCalculateChatboxMaxHeight = () => {
      setChatboxMaxHeight(chatContainerRef.current.clientHeight - chatInputHeight - chatHeaderRef.current.clientHeight);
    };
    window.addEventListener('resize', reCalculateChatboxMaxHeight);
    return () => window.removeEventListener('resize', reCalculateChatboxMaxHeight);
  }, []);

  const fetchNextPageMessageDebounced = fetchNextPageMessage ? debounce(fetchNextPageMessage, 150) : undefined;

  const onChatWindowScroll = (e: any) => {
    const { target } = e;
    const scrollTopPosition = target.scrollTop;
    if (scrollTopPosition >= (target.scrollHeight - target.clientHeight) / 2) {
      isScrollToBottomDisable.current = false;
    } else {
      isScrollToBottomDisable.current = true;
      if (scrollTopPosition < 300 && !oldMessageLoading) {
        fetchNextPageMessageDebounced?.();
      }
    }

    scrollPositionRef.current = scrollTopPosition;
    // if (!loading)
  };

  const sendMessage = useMutationSendMessage();

  const onChatEnterDefault = async ({ chat }: { chat: string }) => {
    if (!chat) return;
    if (onChatEnter) {
      onChatEnter(chat);
      return;
    }
    formRef?.current?.resetForm();
    sendMessage({ message: chat, chatRoomId });
  };
  const [triggerReadMessage] = useReadMessageCall();

  const onChatFocusDefault = () => {
    if (onChatFocus) {
      onChatFocus();
      return;
    }
    if (chatRoom)
      triggerReadMessage({
        chatRoomId: chatRoom.id || '',
        callbackOnSuccess: refetchChatRoomData,
        totalUnreadMessages: chatRoom.unreadMessagesCount,
      });
  };

  const onDeleteImageClick = (id: string) => {
    setChatboxImages((c) => {
      const current = { ...c };
      delete current[id];
      return current;
    });
  };

  const chatboxImagesAsArray = useMemo(() => Object.entries(chatboxImages), [chatboxImages]);

  return {
    onChatFocusDefault,
    onChatEnterDefault,
    onChatWindowScroll,
    formRef,
    chatWindowRef,
    scrollPositionRef,
    isScrollToBottomDisable,
    inputRef,
    chatHeaderRef,
    chatInputHeight,
    chatImageHeight,
    chatboxMaxHeight,
    setChatboxMaxHeight,
    chatboxImages: chatboxImagesAsArray,
    chatContainerRef,
    onDeleteImageClick,
  };
};
